export interface CapturedCameraFrame {
  width: number;
  height: number;
  canvas: CameraCanvas;
}

export interface CameraTrack {
  stop(): void;
}

export interface CameraStream {
  getTracks(): CameraTrack[];
}

export interface CameraVideoFrameSource {
  videoWidth: number;
  videoHeight: number;
}

export interface CameraMediaDevices {
  getUserMedia(constraints: unknown): Promise<CameraStream>;
}

interface CameraCanvasContext {
  drawImage(source: unknown, dx: number, dy: number, width: number, height: number): void;
  translate(x: number, y: number): void;
  scale(x: number, y: number): void;
}

export interface CameraCanvas {
  width: number;
  height: number;
  getContext(type: "2d", options?: { willReadFrequently?: boolean }): CameraCanvasContext | null;
  toBlob(callback: (blob: Blob | null) => void, type?: string, quality?: number): void;
}

export type CameraCanvasFactory = () => CameraCanvas;

interface CameraSessionDependencies {
  requestStream(): Promise<CameraStream>;
  captureFrame(video: CameraVideoFrameSource): CapturedCameraFrame;
}

export interface CameraSession {
  open(): Promise<CameraStream | null>;
  capture(video: CameraVideoFrameSource): CapturedCameraFrame;
  close(): void;
}

export async function requestUserCamera(
  mediaDevices: CameraMediaDevices | undefined,
): Promise<CameraStream> {
  if (!mediaDevices?.getUserMedia) throw new Error("This browser does not support camera capture.");
  return await mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: { ideal: "user" },
      height: { ideal: 1280 },
      width: { ideal: 960 },
    },
  });
}

export function stopCameraStream(stream: CameraStream | null): void {
  for (const track of stream?.getTracks() ?? []) {
    try {
      track.stop();
    } catch {
      // Cleanup is best-effort per track; one broken track must not retain the rest.
    }
  }
}

export function createCameraSession(deps: CameraSessionDependencies): CameraSession {
  let active: CameraStream | null = null;
  let requestVersion = 0;

  const close = () => {
    requestVersion += 1;
    stopCameraStream(active);
    active = null;
  };

  return {
    async open() {
      close();
      const version = requestVersion;
      let stream: CameraStream;
      try {
        stream = await deps.requestStream();
      } catch (error) {
        if (version !== requestVersion) return null;
        throw error;
      }
      if (version !== requestVersion) {
        stopCameraStream(stream);
        return null;
      }
      active = stream;
      return stream;
    },
    capture(video) {
      if (!active) throw new Error("Camera is not active.");
      try {
        return deps.captureFrame(video);
      } finally {
        close();
      }
    },
    close,
  };
}

export function captureCameraFrame(
  video: CameraVideoFrameSource,
  createCanvas: CameraCanvasFactory,
): CapturedCameraFrame {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (width <= 0 || height <= 0) throw new Error("Camera frame is not ready.");

  const canvas = createCanvas();
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Camera frame could not be read.");
  context.drawImage(video, 0, 0, width, height);
  return { width, height, canvas };
}

export async function encodeCameraFrame(
  frame: CapturedCameraFrame,
  mirror: boolean,
  createCanvas: CameraCanvasFactory,
): Promise<Blob> {
  const canvas = mirror ? createCanvas() : frame.canvas;
  if (mirror) {
    canvas.width = frame.width;
    canvas.height = frame.height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Camera photo could not be encoded.");
    context.translate(frame.width, 0);
    context.scale(-1, 1);
    context.drawImage(frame.canvas, 0, 0, frame.width, frame.height);
  }
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Camera photo could not be encoded."));
      },
      "image/jpeg",
      0.92,
    );
  });
}
